import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Activity, ArrowRight, RotateCcw, Target, Zap, ChevronRight, FileSpreadsheet } from 'lucide-react';

const ROLE_LABELS = {
    cs_agent: 'Customer Service',
    mentor: 'Mentors',
    bd_executive: 'Business Development',
};

const RotationHealthWidget = () => {
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [resetDialog, setResetDialog] = useState(null);   // {role}|{connector_id, name, agents}
    const [targetIdx, setTargetIdx] = useState('0');
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await apiClient.get('/round-robin/rotation-health');
            setHealth(r.data);
        } catch (e) {
            if (e.response?.status !== 403) toast.error('Failed to load rotation health');
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const onReset = async () => {
        if (!resetDialog) return;
        setBusy(true);
        try {
            if (resetDialog.role) {
                await apiClient.post('/round-robin/cursor/reset', {
                    role: resetDialog.role,
                    next_idx: parseInt(targetIdx, 10) || 0,
                });
                toast.success(`${ROLE_LABELS[resetDialog.role]} rotation reset`);
            } else if (resetDialog.connector_id) {
                await apiClient.post('/round-robin/cursor/reset-connector', {
                    connector_id: resetDialog.connector_id,
                    next_idx: parseInt(targetIdx, 10) || 0,
                });
                toast.success(`Connector '${resetDialog.name}' rotation reset`);
            }
            setResetDialog(null);
            setTargetIdx('0');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to reset');
        }
        setBusy(false);
    };

    const rolesWithAgents = useMemo(
        () => (health?.roles || []).filter(r => r.total_agents > 0),
        [health]
    );
    const connectorsMulti = useMemo(
        () => (health?.sheet_connectors || []).filter(c => (c.assigned_agent_ids || []).length > 1),
        [health]
    );

    if (loading) {
        return (
            <Card>
                <CardContent className="pt-6">
                    <div className="flex items-center justify-center h-24">
                        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary" />
                    </div>
                </CardContent>
            </Card>
        );
    }
    if (!health) return null;

    return (
        <>
            <Card data-testid="rotation-health-widget">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Activity className="h-5 w-5 text-emerald-500" />
                        Rotation Health
                    </CardTitle>
                    <CardDescription>
                        Strict cursor-based rotation. Last 7 days' counts. Arrow ← = next agent in line. Reset advances the pointer.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {rolesWithAgents.map(r => (
                        <div key={r.role} className="border border-border rounded-lg p-4 bg-muted/20" data-testid={`rotation-role-${r.role}`}>
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <p className="font-semibold">{ROLE_LABELS[r.role] || r.role}</p>
                                    <p className="text-xs text-muted-foreground">
                                        Cursor: <span className="font-mono">{r.cursor_val}</span> → next: <span className="text-primary font-medium">{r.next_agent?.full_name || '—'}</span>
                                    </p>
                                </div>
                                <Button size="sm" variant="outline" onClick={() => { setResetDialog({ role: r.role, agents: r.agents, label: ROLE_LABELS[r.role] }); setTargetIdx(String(r.next_idx)); }} data-testid={`reset-btn-${r.role}`}>
                                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
                                </Button>
                            </div>
                            <div className="space-y-1.5">
                                {r.agents.map(a => (
                                    <div key={a.id} className={`flex items-center gap-2 text-sm py-1 px-2 rounded ${a.is_next ? 'bg-primary/10 border border-primary/30' : ''}`}>
                                        <span className="w-6 text-xs text-muted-foreground font-mono">{a.idx}</span>
                                        {a.is_next && <ArrowRight className="h-3 w-3 text-primary shrink-0" />}
                                        <span className={`flex-1 ${a.is_next ? 'font-medium' : ''}`}>{a.full_name}</span>
                                        <Badge variant="outline" className="text-[10px]">{a.week_count} assigned · 7d</Badge>
                                        {a.last_assigned_at && (
                                            <span className="text-[10px] text-muted-foreground hidden md:inline">
                                                last: {a.last_assigned_at.slice(0, 10)}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    {connectorsMulti.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <FileSpreadsheet className="h-4 w-4 text-blue-500" />
                                <p className="font-semibold">Google Sheet Connectors (multi-agent)</p>
                            </div>
                            <div className="grid md:grid-cols-2 gap-3">
                                {connectorsMulti.map(c => {
                                    const agents = (c.assigned_agent_names || []).slice().sort((a, b) => (a || '').localeCompare(b || ''));
                                    const nxt = agents.length ? agents[(c.next_agent_idx || 0) % agents.length] : '—';
                                    return (
                                        <div key={c.id} className="border border-border rounded-lg p-3 bg-muted/10" data-testid={`rotation-connector-${c.id}`}>
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="text-sm font-medium truncate">{c.name}</p>
                                                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setResetDialog({ connector_id: c.id, name: c.name, agents: agents.map((n, i) => ({ idx: i, full_name: n })) }); setTargetIdx('0'); }} data-testid={`reset-connector-btn-${c.id}`}>
                                                    <RotateCcw className="h-3 w-3" />
                                                </Button>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                <Target className="h-3 w-3" />
                                                <span>cursor: {c.next_agent_idx ?? 0}</span>
                                                <ChevronRight className="h-3 w-3" />
                                                <span className="text-primary font-medium truncate">{nxt}</span>
                                            </div>
                                            <div className="mt-1.5 flex flex-wrap gap-1">
                                                {agents.map((n, i) => (
                                                    <Badge key={i} variant={i === ((c.next_agent_idx || 0) % agents.length) ? 'default' : 'outline'} className="text-[10px]">
                                                        {n}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Reset dialog */}
            <Dialog open={!!resetDialog} onOpenChange={(o) => { if (!o) setResetDialog(null); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Reset Rotation Cursor</DialogTitle>
                        <DialogDescription>
                            {resetDialog?.role
                                ? <>Reset rotation for <b>{resetDialog.label}</b>. Next assignment will go to the agent at the chosen slot.</>
                                : <>Reset rotation for connector <b>{resetDialog?.name}</b>. Useful when you just added agents and want the next lead to go to a specific slot.</>
                            }
                        </DialogDescription>
                    </DialogHeader>
                    {resetDialog && (
                        <div className="space-y-3">
                            <div>
                                <label className="text-sm font-medium">Next agent slot</label>
                                <Input type="number" min="0" max={(resetDialog.agents?.length || 1) - 1} value={targetIdx} onChange={e => setTargetIdx(e.target.value)} data-testid="reset-target-idx" />
                                <p className="text-xs text-muted-foreground mt-1">
                                    Must be between 0 and {(resetDialog.agents?.length || 1) - 1}.
                                </p>
                            </div>
                            <div className="border border-border rounded-lg p-3 bg-muted/20 space-y-1 max-h-48 overflow-y-auto">
                                {(resetDialog.agents || []).map(a => {
                                    const isSel = String(a.idx) === String(targetIdx);
                                    return (
                                        <button key={a.idx} type="button" onClick={() => setTargetIdx(String(a.idx))}
                                                className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 transition-colors ${isSel ? 'bg-primary/20 border border-primary/40' : 'hover:bg-muted'}`}
                                                data-testid={`agent-slot-${a.idx}`}>
                                            <span className="font-mono text-xs w-6">{a.idx}</span>
                                            {isSel && <Zap className="h-3 w-3 text-primary" />}
                                            <span>{a.full_name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setResetDialog(null)}>Cancel</Button>
                        <Button onClick={onReset} disabled={busy} data-testid="confirm-reset-btn">{busy ? 'Resetting…' : 'Reset'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default RotationHealthWidget;
